import React from "react";
import { Box, alpha, keyframes, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../Store";

/**
 * Global API loading indicator.
 * Shows a subtle branded top-bar when any RTK Query request is in flight.
 * Place this once in the app root.
 */

const slideProgress = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

const GlobalLoader: React.FC = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  // RTK Query tracks all pending queries/mutations in its reducer
  const isFetching = useSelector((state: RootState) => {
    const api = (state as any).attoDeskApi;
    if (!api?.queries && !api?.mutations) return false;
    const queries = api.queries ? Object.values(api.queries) : [];
    const mutations = api.mutations ? Object.values(api.mutations) : [];
    return [...queries, ...mutations].some((q: any) => q?.status === "pending");
  });

  if (!isFetching) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        bgcolor: alpha(primary, 0.08),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "30%",
          height: "100%",
          background: `linear-gradient(90deg, transparent, ${primary}, transparent)`,
          animation: `${slideProgress} 1.2s ease-in-out infinite`,
        }}
      />
    </Box>
  );
};

export default GlobalLoader;

/**
 * fetch() wrapper used by the Lead/CRM raw-fetch API clients (which don't go
 * through RTK Query). Step-Up's global loader is RTK-driven, so this is a thin
 * passthrough today — swap in loader-counter increment/decrement here if you
 * want raw-fetch calls to drive the top-bar indicator too.
 */
export const trackedFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
  fetch(input, init);
